import { useState, useEffect } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { toBase64 } from '../../../utils/utils';
import ProductInformationManualEntry from '../components/ProductInformationManualEntry';
import ImportProductFromLinkHeader from '../components/ImportProductFromLinkHeader';
import ImportProductFromLink from '../components/ImportProductFromLink';
import RenderEntryMethodSelection from '../components/RenderEntryMethodSelection';
import SearchPreviousProducts from '../components/SearchPreviousProducts';
import ButtonResetTypeProduct from '../components/ButtonResetTypeProdct';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';

export enum TypeProduct {
  NONE = 'none',
  LINK = 'link',
  MANUAL = 'manual',
  PREVIOUS = 'previous'
}

export function StepProductData() {
  const {
    data,
    updateProductData,
    updateVisualContent,
    previousProducts,
    isPreviousProductsLoading,
    fetchPreviousProducts
  } = useOnboardingStore();
  const { productData } = data;
  const { language } = useAuth();
  
  const [productLink, setProductLink] = useState<string>(productData.productLink || '');
  const [typeProduct, setTypeProduct] = useState<TypeProduct>(TypeProduct.NONE);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!productData.warranty) {
      updateProductData({
        ...productData,
        warranty: '30days',
        customWarrantyDays: '30',
        freeShipping: productData.freeShipping ?? true
      });
    }
  }, []);


  useEffect(() => {
    fetchPreviousProducts();
  }, []);

  const handleManualEntry = () => {
    setTypeProduct(TypeProduct.MANUAL);
    updateProductData({
      ...productData,
      name: '',
      price: '',
      details: '',
      isManualEntry: true,
      productLink: '',
      providerId: null,
      externalId: null
    });

    updateVisualContent({
      images: [],
    });
  };

  const handleLinkEntry = () => {
    setTypeProduct(TypeProduct.LINK);
    setProductLink('');
    updateProductData({
      ...productData,
      name: '',
      price: '',
      details: '',
      isManualEntry: false,
      productLink: ''
    });

    updateVisualContent({
      images: [],
    });
  };

  const handleImageUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;


    fileInput.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;

      const base64Images = await Promise.all(
        Array.from(files).map(file => toBase64(file))
      );

      updateVisualContent({
        imagesBase64: [...data.visualContent.imagesBase64, ...base64Images]
      });
    };

    fileInput.click();
  };

  const removeImage = (index: number) => {
    const newImages = [...data.visualContent.images];
    newImages.splice(index, 1);
    const newImagesBase64 = [...data.visualContent.imagesBase64];
    newImagesBase64.splice(index, 1);
    updateVisualContent({ images: newImages, imagesBase64: newImagesBase64 });
  };

  const selectMainImage = (index: number) => {
    setSelectedImageIndex(index);

    const { visualContent } = data;
    const newImages = [...visualContent.images];
    const selectedImage = newImages[index];
    newImages.splice(index, 1);
    newImages.unshift(selectedImage);

    updateVisualContent({
      images: newImages,
    });

    setSelectedImageIndex(0);
  };

  const handleProductLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProductLink(value);

    updateProductData({
      ...productData,
      productLink: value
    });
  };
  
  const handlePreviousProductSelectType = () => {
    setTypeProduct(TypeProduct.PREVIOUS);
  };

  const handlePreviousProductSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const productId = e.target.value;
    if (!productId) return;
    const selectedProduct = previousProducts.find(product => product.id === productId);
    if (!selectedProduct) return;

    updateProductData({
      name: selectedProduct.name,
      price: selectedProduct.price.toString(),
      details: selectedProduct.description,
      isManualEntry: false,
      externalId: selectedProduct.id,
      providerId: 'INTERNAL_FLUXI'
    });

    if (selectedProduct.images && selectedProduct.images.length > 0) {
      updateVisualContent({
        images: selectedProduct.images
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4">
        <h3 className="text-xl font-medium mb-6">{dictionary["select-a-product-for-your-sales-funnel"][language]}</h3>
        {typeProduct !== TypeProduct.NONE && (
          <ButtonResetTypeProduct
            onClick={() => setTypeProduct(TypeProduct.NONE)}
          />
        )}
      </div>

      {typeProduct === TypeProduct.NONE && (
        <RenderEntryMethodSelection
          typeProduct={typeProduct} 
          handleLinkEntry={handleLinkEntry} 
          handleManualEntry={handleManualEntry} 
          handlePreviousProductSelectType={handlePreviousProductSelectType} 
        />
      )}

      {typeProduct === TypeProduct.LINK && (
        <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
          {!isLoading && (
            <ImportProductFromLinkHeader />
          )}
          <ImportProductFromLink
            isLoading={isLoading}
            productLink={productLink}
            handleProductLinkChange={handleProductLinkChange}
            error={error}
          />
        </div>
      )}

      {typeProduct === TypeProduct.MANUAL && (
        <ProductInformationManualEntry
          data={data}
          handleImageUpload={handleImageUpload}
          selectMainImage={selectMainImage}
          removeImage={removeImage}
          selectedImageIndex={selectedImageIndex}
          productData={productData}
          updateProductData={updateProductData}
        />
      )}

      {typeProduct === TypeProduct.PREVIOUS && (
        <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
          <SearchPreviousProducts
            previousProducts={previousProducts}
            handlePreviousProductSelect={handlePreviousProductSelect}
            isPreviousProductsLoading={isPreviousProductsLoading}
          />
        </div>
      )}

    </div>
  );
}